import { useState, useEffect, useRef, useCallback } from 'react';
import { Calendar, Clock, ChevronUp, ChevronDown, X } from 'lucide-react';
import { useThemeStore } from '../store/theme';

interface DateTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  minDate?: string;
  placeholder?: string;
  className?: string;
}

// Get user's preferred hour format from localStorage or system preference
const getPreferredHourFormat = (): '12' | '24' => {
  const saved = localStorage.getItem('preferredHourFormat');
  if (saved === '12' || saved === '24') return saved;

  // Try to detect from system locale
  const testDate = new Date(2024, 0, 1, 13, 0);
  const formatted = testDate.toLocaleTimeString();
  // If it contains PM/AM or pm/am, it's likely 12-hour format
  if (formatted.toLowerCase().includes('pm') || formatted.toLowerCase().includes('am')) {
    return '12';
  }
  return '24';
};

export default function DateTimePicker({
  value,
  onChange,
  minDate,
  placeholder = 'Select date and time',
  className = '',
}: DateTimePickerProps) {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const containerRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [hourFormat, setHourFormat] = useState<'12' | '24'>(getPreferredHourFormat);

  // Parse current value
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    if (value) {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  });

  // Time state for the picker
  const [pickerHour, setPickerHour] = useState(12);
  const [pickerMinute, setPickerMinute] = useState(0);
  const [pickerPeriod, setPickerPeriod] = useState<'AM' | 'PM'>('AM');
  const [pickerDate, setPickerDate] = useState(() => {
    const now = new Date();
    return {
      year: now.getFullYear(),
      month: now.getMonth(),
      day: now.getDate(),
    };
  });

  // Sync picker state when value changes
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        setSelectedDate(date);
        setPickerDate({
          year: date.getFullYear(),
          month: date.getMonth(),
          day: date.getDate(),
        });

        let hours = date.getHours();
        if (hourFormat === '12') {
          setPickerPeriod(hours >= 12 ? 'PM' : 'AM');
          hours = hours % 12;
          if (hours === 0) hours = 12;
        }
        setPickerHour(hours);
        setPickerMinute(date.getMinutes());
      }
    }
  }, [value, hourFormat]);

  // Save hour format preference
  const toggleHourFormat = () => {
    const newFormat = hourFormat === '12' ? '24' : '12';
    setHourFormat(newFormat);
    localStorage.setItem('preferredHourFormat', newFormat);

    // Convert current hour to new format
    if (newFormat === '12') {
      // Convert from 24 to 12
      let h = pickerHour;
      if (h === 0) {
        setPickerHour(12);
        setPickerPeriod('AM');
      } else if (h === 12) {
        setPickerHour(12);
        setPickerPeriod('PM');
      } else if (h > 12) {
        setPickerHour(h - 12);
        setPickerPeriod('PM');
      } else {
        setPickerPeriod('AM');
      }
    } else {
      // Convert from 12 to 24
      let h = pickerHour;
      if (pickerPeriod === 'PM' && h !== 12) {
        h += 12;
      } else if (pickerPeriod === 'AM' && h === 12) {
        h = 0;
      }
      setPickerHour(h);
    }
  };

  // Build datetime string from picker state
  const buildDateTimeString = useCallback((
    year: number,
    month: number,
    day: number,
    hour: number,
    minute: number,
    period: 'AM' | 'PM',
    format: '12' | '24'
  ): string => {
    let h = hour;
    if (format === '12') {
      if (period === 'PM' && h !== 12) {
        h += 12;
      } else if (period === 'AM' && h === 12) {
        h = 0;
      }
    }

    const date = new Date(year, month, day, h, minute);
    return date.toISOString();
  }, []);

  // Apply changes and close
  const applyChanges = () => {
    const isoString = buildDateTimeString(
      pickerDate.year,
      pickerDate.month,
      pickerDate.day,
      pickerHour,
      pickerMinute,
      pickerPeriod,
      hourFormat
    );
    onChange(isoString);
    setIsOpen(false);
  };

  // Format display value
  const formatDisplayValue = (): string => {
    if (!selectedDate) return '';

    const dateOptions: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    };

    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: hourFormat === '12',
    };

    return `${selectedDate.toLocaleDateString('en-US', dateOptions)} at ${selectedDate.toLocaleTimeString('en-US', timeOptions)}`;
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const firstDay = new Date(pickerDate.year, pickerDate.month, 1);
    const lastDay = new Date(pickerDate.year, pickerDate.month + 1, 0);
    const startDay = firstDay.getDay();
    const totalDays = lastDay.getDate();

    const days: (number | null)[] = [];

    // Add empty slots for days before the first
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }

    // Add actual days
    for (let i = 1; i <= totalDays; i++) {
      days.push(i);
    }

    return days;
  };

  // Check if a day is disabled (before min date)
  const isDayDisabled = (day: number): boolean => {
    if (!minDate) return false;
    const minDateObj = new Date(minDate);
    const checkDate = new Date(pickerDate.year, pickerDate.month, day);
    return checkDate < new Date(minDateObj.setHours(0, 0, 0, 0));
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Month names
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Input Display */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-colors ${
          isDark
            ? 'bg-dark-900 border-border text-white hover:border-accent-purple/50'
            : 'bg-white border-slate-200 text-slate-900 hover:border-accent-purple/50'
        }`}
      >
        <div className="flex items-center gap-2">
          <Calendar className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
          <span className={selectedDate ? '' : isDark ? 'text-text-muted' : 'text-slate-400'}>
            {selectedDate ? formatDisplayValue() : placeholder}
          </span>
        </div>
        <Clock className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
      </button>

      {/* Dropdown Picker */}
      {isOpen && (
        <div className={`absolute z-50 mt-2 w-full min-w-[320px] p-4 rounded-2xl border shadow-xl ${
          isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200'
        }`}>
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => {
                const newMonth = pickerDate.month - 1;
                if (newMonth < 0) {
                  setPickerDate({ ...pickerDate, month: 11, year: pickerDate.year - 1 });
                } else {
                  setPickerDate({ ...pickerDate, month: newMonth });
                }
              }}
              className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
            >
              <ChevronUp className="w-4 h-4 rotate-[-90deg]" />
            </button>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {months[pickerDate.month]} {pickerDate.year}
            </span>
            <button
              type="button"
              onClick={() => {
                const newMonth = pickerDate.month + 1;
                if (newMonth > 11) {
                  setPickerDate({ ...pickerDate, month: 0, year: pickerDate.year + 1 });
                } else {
                  setPickerDate({ ...pickerDate, month: newMonth });
                }
              }}
              className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
            >
              <ChevronUp className="w-4 h-4 rotate-90" />
            </button>
          </div>

          {/* Day Labels */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
              <div key={day} className={`text-center text-xs py-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {generateCalendarDays().map((day, index) => (
              <button
                key={index}
                type="button"
                disabled={day === null || isDayDisabled(day)}
                onClick={() => day && setPickerDate({ ...pickerDate, day })}
                className={`
                  w-9 h-9 rounded-lg text-sm font-medium transition-colors
                  ${day === null ? 'invisible' : ''}
                  ${day && isDayDisabled(day) ? 'opacity-30 cursor-not-allowed' : ''}
                  ${day === pickerDate.day
                    ? 'bg-accent-purple text-white'
                    : isDark
                      ? 'text-white hover:bg-dark-700'
                      : 'text-slate-900 hover:bg-slate-100'
                  }
                `}
              >
                {day}
              </button>
            ))}
          </div>

          {/* Time Picker */}
          <div className={`border-t pt-4 ${isDark ? 'border-border' : 'border-slate-200'}`}>
            <div className="flex items-center justify-between mb-3">
              <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Time
              </span>
              <button
                type="button"
                onClick={toggleHourFormat}
                className={`text-xs px-2 py-1 rounded-md ${
                  isDark ? 'bg-dark-700 text-text-secondary hover:bg-dark-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {hourFormat === '12' ? '12-hour' : '24-hour'}
              </button>
            </div>

            <div className="flex items-center justify-center gap-2">
              {/* Hours */}
              <div className="flex flex-col items-center">
                <button
                  type="button"
                  onClick={() => {
                    const maxHour = hourFormat === '12' ? 12 : 23;
                    const minHour = hourFormat === '12' ? 1 : 0;
                    setPickerHour(prev => prev >= maxHour ? minHour : prev + 1);
                  }}
                  className={`p-1 rounded ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <input
                  type="text"
                  value={pickerHour.toString().padStart(2, '0')}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    const maxHour = hourFormat === '12' ? 12 : 23;
                    const minHour = hourFormat === '12' ? 1 : 0;
                    if (!isNaN(val) && val >= minHour && val <= maxHour) {
                      setPickerHour(val);
                    }
                  }}
                  className={`w-12 text-center text-lg font-mono py-2 rounded-lg border ${
                    isDark ? 'bg-dark-900 border-border text-white' : 'bg-slate-50 border-slate-200 text-slate-900'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => {
                    const maxHour = hourFormat === '12' ? 12 : 23;
                    const minHour = hourFormat === '12' ? 1 : 0;
                    setPickerHour(prev => prev <= minHour ? maxHour : prev - 1);
                  }}
                  className={`p-1 rounded ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>

              <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>:</span>

              {/* Minutes */}
              <div className="flex flex-col items-center">
                <button
                  type="button"
                  onClick={() => setPickerMinute(prev => prev >= 59 ? 0 : prev + 1)}
                  className={`p-1 rounded ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <input
                  type="text"
                  value={pickerMinute.toString().padStart(2, '0')}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (!isNaN(val) && val >= 0 && val <= 59) {
                      setPickerMinute(val);
                    }
                  }}
                  className={`w-12 text-center text-lg font-mono py-2 rounded-lg border ${
                    isDark ? 'bg-dark-900 border-border text-white' : 'bg-slate-50 border-slate-200 text-slate-900'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setPickerMinute(prev => prev <= 0 ? 59 : prev - 1)}
                  className={`p-1 rounded ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>

              {/* AM/PM for 12-hour format */}
              {hourFormat === '12' && (
                <div className="flex flex-col items-center ml-2">
                  <button
                    type="button"
                    onClick={() => setPickerPeriod(prev => prev === 'AM' ? 'PM' : 'AM')}
                    className={`p-1 rounded ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <div className={`w-12 text-center text-lg font-mono py-2 rounded-lg border ${
                    isDark ? 'bg-dark-900 border-border text-white' : 'bg-slate-50 border-slate-200 text-slate-900'
                  }`}>
                    {pickerPeriod}
                  </div>
                  <button
                    type="button"
                    onClick={() => setPickerPeriod(prev => prev === 'AM' ? 'PM' : 'AM')}
                    className={`p-1 rounded ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-border">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className={`px-4 py-2 rounded-xl text-sm font-medium ${
                isDark ? 'hover:bg-dark-700 text-text-secondary' : 'hover:bg-slate-100 text-slate-600'
              }`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={applyChanges}
              className="px-4 py-2 bg-accent-purple hover:bg-accent-purple/90 text-white rounded-xl text-sm font-medium"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
